import { Head } from "@/components/Head";
import { Button } from "@/components/Button";
import { CtaBand } from "@/components/CtaBand";
import { Eyebrow, Section, SectionHeading } from "@/components/Section";
import { HeroItem, HeroStagger, Reveal } from "@/components/Reveal";

const storyPoints = [
  "The CSR sees the same job the technician sees",
  "The AI voice agent books straight into the same dispatch board",
  "Invoices, memberships, and inventory read from one truth, not four",
];

const beliefs = [
  {
    number: "01",
    title: "One system, not a stack of tabs",
    body: "Office, field, and phone share one data model. A job an AI voice agent books at 2am looks identical to one a CSR builds by hand — same record, same history, same board.",
    span: "lg:col-span-2",
  },
  {
    number: "02",
    title: "AI answers the phone; people close the relationship",
    body: "Every call gets picked up and every lead gets logged. Voice agents handle intake and booking — your team keeps the judgment calls, the escalations, and the customers.",
    span: "",
  },
  {
    number: "03",
    title: "Multi-brand is a first-class citizen",
    body: "Groups running two, three, or six brands under one roof get shared visibility without merging customer lists or losing brand identity on the invoice.",
    span: "",
  },
  {
    number: "04",
    title: "Built around how service work actually happens",
    body: "Diagnostics, parts, photos, and payments happen in the truck, not at a desk. TrexField is built for that reality, not adapted from a generic project-management tool.",
    span: "lg:col-span-2",
  },
];

const stats = [
  {
    value: "3",
    label: "Core layers",
    detail:
      "Office, field, and voice — one data model, not three exports to reconcile.",
  },
  {
    value: "4",
    label: "Trades built in",
    detail:
      "HVAC, plumbing, electrical, and roofing workflows, not a generic form builder.",
  },
  {
    value: "24/7",
    label: "Voice coverage",
    detail:
      "AI agents pick up after-hours, overflow, and every call in between.",
  },
  {
    value: "1",
    label: "Source of truth",
    detail: "A job is entered once and read everywhere it needs to be seen.",
  },
];

export function AboutPage() {
  return (
    <>
      <Head
        title="About ServiTrex — The operating system for home-service businesses"
        description="ServiTrex unifies office operations, TrexField for technicians, and AI voice agents into one platform for HVAC, plumbing, electrical, and roofing companies. A Cognautic company."
      />

      <section className="relative overflow-hidden pb-10 pt-12 sm:pb-16 sm:pt-16 lg:pt-20">
        <div className="container-page">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-8">
            <HeroStagger>
              <HeroItem>
                <Eyebrow>About ServiTrex</Eyebrow>
              </HeroItem>
              <HeroItem>
                <h1 className="max-w-3xl text-balance text-4xl text-fg sm:text-5xl lg:text-[3.4rem] lg:leading-[1.03]">
                  We build the operating system for home-service businesses.
                </h1>
              </HeroItem>
              <HeroItem>
                <p className="mt-5 max-w-xl text-pretty text-lg text-muted sm:text-xl">
                  Not another app to log into. The system the office runs on,
                  the technicians work from, and the phone line answers through
                  — all reading and writing to the same job.
                </p>
              </HeroItem>
              <HeroItem>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button to="/demo" size="lg">
                    Book a demo
                  </Button>
                  <Button to="/product/office" variant="secondary" size="lg">
                    See the platform
                  </Button>
                </div>
              </HeroItem>
            </HeroStagger>

            <Reveal>
              <div className="relative mx-auto max-w-md lg:ml-auto lg:mr-0 lg:max-w-none">
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-6 -top-8 hidden h-32 w-32 rounded-full bg-teal/20 blur-2xl sm:block"
                />
                <img
                  src="/img/mktg-hero-dashboard.jpg"
                  alt="ServiTrex dispatch dashboard showing live jobs, technician capacity, and brand-scoped boards"
                  width={1376}
                  height={768}
                  className="relative aspect-[16/9] w-full rounded-[1.75rem] border border-border object-cover shadow-soft"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div>
            <Eyebrow>Why we exist</Eyebrow>
            <h2 className="text-balance text-3xl text-fg sm:text-4xl lg:text-[2.6rem]">
              Owners are drowning in disconnected tools.
            </h2>
            <p className="mt-5 text-pretty text-muted sm:text-lg">
              A phone system that doesn&rsquo;t talk to the CRM. A field app
              that doesn&rsquo;t talk to invoicing. A separate tool for
              memberships, another for marketing, and a spreadsheet holding the
              whole thing together. Every gap between those tools is a place a
              job falls through, a call goes unanswered, or a technician shows
              up blind.
            </p>
            <p className="mt-4 text-pretty text-muted sm:text-lg">
              We didn&rsquo;t set out to build another point solution. We set
              out to build the one system a home-service company runs on —
              office, field, and the phone line that never stops ringing.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-fg">
              {storyPoints.map((point) => (
                <li key={point} className="flex gap-2">
                  <span className="text-teal-deep">→</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          <Reveal delay={0.08}>
            <div className="relative mx-auto max-w-md lg:mx-0">
              <img
                src="/img/mktg-field-tech.jpg"
                alt="Field technician checking a job on a mobile phone at a customer's property"
                width={1200}
                height={896}
                loading="lazy"
                className="aspect-[4/3] w-full rounded-[1.75rem] border border-border object-cover shadow-soft"
              />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-surface-muted/50">
        <SectionHeading
          eyebrow="What we believe"
          title="A short list of things we won't compromise on"
          description="These are the calls we make when a feature request pulls in the opposite direction."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-2">
          {beliefs.map((belief, index) => (
            <Reveal
              key={belief.title}
              delay={index * 0.05}
              className={belief.span}
            >
              <article className="flex h-full flex-col rounded-[1.75rem] border border-border bg-surface-raised p-6 shadow-soft sm:p-7">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-deep">
                  {belief.number}
                </p>
                <h3 className="mt-3 font-display text-2xl tracking-tight">
                  {belief.title}
                </h3>
                <p className="mt-3 flex-1 text-pretty text-muted">
                  {belief.body}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <Reveal>
            <img
              src="/img/mktg-ai-voice.jpg"
              alt="Visualization of an AI voice agent answering an inbound customer call"
              width={1200}
              height={896}
              loading="lazy"
              className="aspect-[4/3] w-full rounded-[1.75rem] border border-border object-cover shadow-soft"
            />
          </Reveal>

          <div>
            <SectionHeading
              eyebrow="The platform at a glance"
              title="What 'one system' actually means"
              description="Not a slogan — the shape of the data model underneath every screen."
            />
            <dl className="mt-8 grid grid-cols-2 gap-5">
              {stats.map((stat, index) => (
                <Reveal key={stat.label} delay={index * 0.05}>
                  <div className="h-full rounded-2xl border border-border bg-surface-raised p-5">
                    <dt className="font-display text-3xl font-bold tracking-tight text-fg sm:text-4xl">
                      {stat.value}
                    </dt>
                    <dd className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-teal-deep">
                      {stat.label}
                    </dd>
                    <p className="mt-2 text-sm leading-relaxed text-muted">
                      {stat.detail}
                    </p>
                  </div>
                </Reveal>
              ))}
            </dl>
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        <Reveal>
          <div className="rounded-[1.75rem] border border-dashed border-border bg-surface-raised/70 px-6 py-8 text-center sm:px-10">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-teal-deep">
              Who&rsquo;s behind it
            </p>
            <p className="mx-auto mt-3 max-w-2xl text-pretty text-lg text-fg sm:text-xl">
              ServiTrex is a Cognautic company. Cognautic builds focused,
              vertical software for operators who&rsquo;d rather run their
              business than manage a stack of disconnected apps — ServiTrex is
              where that focus meets the trades.
            </p>
          </div>
        </Reveal>
      </Section>

      <CtaBand
        title="See ServiTrex answer a call, book a job, and close it out"
        body="Walk the office board, TrexField job flow, and AI voice intake with a specialist who knows HVAC, plumbing, electrical, and roofing operations."
      />
    </>
  );
}
